import { useRouter } from "next/router";
import { useEffect, useRef, useState, useCallback } from "react";
import NextLink from "commons/ui/NextLink";
import PartnerLayout from "commons/ui/Partner/PartnerLayout";
import { ModalOverlay, ModalNextArrow, ModalPrevArrow } from "commons/ModalOverlay";
import type { PartnerResult, PartnerResults } from "components/PartnersFilter";
import { Button } from "@chakra-ui/react";
import { pxToRem } from "themes/utils/mapPxToRem";

interface PartnerOverlayProps {
    partners: PartnerResults;
}

const PartnerOverlay = ({ partners }: PartnerOverlayProps) => {
    const { query, push } = useRouter();
    const [partner, setPartner] = useState<PartnerResult | null>();
    const [prevPartnerId, setPrevPartnerId] = useState<string>();
    const [nextPartnerId, setNextPartnerId] = useState<string>();
    const [modalImageLoading, setModalImageLoading] = useState(false);
    const modalRef = useRef<HTMLDivElement>(null);
    const closeOverlay = () => {
        setPartner(null);
        delete query.highlight;
        push({ query }, undefined, { shallow: true });
    };

    // Calculate previous and next partner based on currently selected and current search results displayed
    useEffect(() => {
        if (!partners?.results || partners.results.length === 0) return;

        const activePartnerIndex = partners.results.findIndex((res) => res.id === query?.highlight);
        let nextIndex = activePartnerIndex + 1;
        let prevIndex = activePartnerIndex - 1;

        if (nextIndex >= partners.results.length) {
            nextIndex = 0;
        }
        if (prevIndex < 0) {
            prevIndex = partners.results.length - 1;
        }

        setNextPartnerId(partners.results[nextIndex].id);
        setPrevPartnerId(partners.results[prevIndex].id);
    }, [partners, query]);

    // Get current partner data from server
    useEffect(() => {
        const getPartner = async () => {
            setModalImageLoading(true);
            const response = await fetch(`/api/partner/?id=${query.highlight}`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                },
            });

            if (!response.ok) {
                throw new Error("Failed to fetch data");
            }

            const responseData = await response.json();
            setPartner(responseData.result as PartnerResult);
            setModalImageLoading(false);

            if (modalRef.current) {
                modalRef.current.scrollTo(0, 0);
            }
        };

        if (!query.highlight) return;

        getPartner();
    }, [query.highlight]);

    // Handle keyboard navigation
    const handleKeyDown = useCallback(
        (event: KeyboardEvent) => {
            if (event.key === "ArrowLeft") {
                // Navigate to previous partner
                if (prevPartnerId) {
                    push({ query: { ...query, highlight: prevPartnerId } }, undefined, {
                        shallow: true,
                    });
                }
            } else if (event.key === "ArrowRight" && nextPartnerId) {
                // Navigate to next partner
                push({ query: { ...query, highlight: nextPartnerId } }, undefined, {
                    shallow: true,
                });
            }
        },
        [prevPartnerId, nextPartnerId, push, query]
    );

    // Add and remove keyboard event listener
    useEffect(() => {
        if (query.highlight) {
            window.addEventListener("keydown", handleKeyDown);
        }

        return () => {
            window.removeEventListener("keydown", handleKeyDown);
        };
    }, [query.highlight, handleKeyDown]);

    if (!query.highlight || !partner) return null;

    return (
        <ModalOverlay
            modalRef={modalRef}
            closeOverlay={closeOverlay}
            next={
                <NextLink
                    href={{
                        query: { ...query, highlight: prevPartnerId },
                    }}
                >
                    <Button
                        w={pxToRem("40px")}
                        h={pxToRem("40px")}
                        variant="unstyled"
                        isLoading={modalImageLoading}
                        leftIcon={<ModalNextArrow />}
                    ></Button>
                </NextLink>
            }
            prev={
                <NextLink
                    href={{
                        query: { ...query, highlight: nextPartnerId },
                    }}
                >
                    <Button
                        w={pxToRem("40px")}
                        h={pxToRem("40px")}
                        variant="unstyled"
                        isLoading={modalImageLoading}
                        leftIcon={<ModalPrevArrow />}
                    ></Button>
                </NextLink>
            }
        >
            <PartnerLayout {...partner} />
        </ModalOverlay>
    );
};
export default PartnerOverlay;
